import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { PershingError, LogService, IAuthenticationService, ServiceRouter } from '@pershing/pershing-core';
import { EdeliveryServiceDefinition } from './../models/router-service-definition';
import { DocsIncludedResponse } from './../models/docs-included-response.model';
import { AccountsResult } from './../models/accounts-result.model';
import { EntitlementsResult } from './../models/entitlements-result.model';
import { Account } from './../models/account.model';
import { defaultAccountsResult } from './../models/accounts-result.model';
import { Context } from './../models/context.model';
import { PreferencesRequest } from './../models/preference-request.model';

import { HttpHeaders } from '@angular/common/http';
import { EdeliveryLabels } from '../i18n/edelivery-labels';
import { Entitlements } from '../models/entitlements.model';

let headers = new HttpHeaders({
    'Content-Type': 'application/json'
});

/**
 * @desc interacts with the backend services to fetch the data and updates the grid.
 */
@Injectable()
export class EDeliveryService {
    private baseUrl = '';
    listOfAccountsChanged: Account[] = [];
    backButtonClicked: boolean = false;
    showAgreement$: Observable<boolean>;
    private _showAgreement: BehaviorSubject<boolean>;
    trackingList = [];
    listToBeSubmitted = [];
    previousSettings: any[] = [];
    currentSettings: any[] = [];
    savedSettings: any[] = [];
    
    externalEmail = '';
    quickEnrollUpdatedEmail = '';
    quickEnrollWarningMessage = '';
    isEmailChanged = false;
    isMobile = false;

    displayProspectusQuickEnroll = false;

    previouslyEnrolled = new Map<String, number>();
    changeView$: Observable<any>;
    allDocNamesCurrentlyDisplayed: any = [];

    accountsMap = new Map<string, Object[]>();
    finalListOfAccounts: Account[] = [];
    accountsWithChangedEmails: Account[] = [];
    stateCopyOfAccounts: Account[] = [];
    firstCellAccount: boolean = true;

    displayQPRDisclaimer: boolean = false;
    displayCommunicationTypesDisclaimer: boolean = false;
    listOfEligibleDocs = new Set();

    // documentsPending: boolean = false;
    isQuickEnrollDisplayed = false;
    prefPopupAlreadyTriggered = false;

    // disableEnrollButton: boolean = true;
    disableEnrollButton$: Observable<boolean>;
    private _disableEnrollButton: BehaviorSubject<boolean>;
    accountsChangedMap = new Map<string, boolean>();

    // Document names labels
    statementsLabel = EdeliveryLabels.STATEMENTS;
    tradeLabel = EdeliveryLabels.TRADE;
    QPRLabel = EdeliveryLabels.QPR;
    notificationsLabel = EdeliveryLabels.NOTIFICATIONS;
    taxLabel = EdeliveryLabels.TAX;
    prospectusLabel = EdeliveryLabels.PROSPECTUS;
    proxyLabel = EdeliveryLabels.PROXY;

    originalResponse: any;

    updatePreferencesInProgressFlag: boolean = false;
    preferencesSubmitted: boolean = false;
    isUserEnrolling: boolean = false;
    preferencesResponse$: Observable<AccountsResult>;
    preferencesSaved$: Observable<boolean>;
    preferencesConfirmed$: Observable<boolean>;
    preferencesConfirmedMobile$: Observable<boolean>;
    error$: Observable<PershingError>;
    loading$: Observable<boolean>; // Remove this in Sprint 12 when timer is removed
    confirmationResponse$: Observable<AccountsResult>;
    docsIncludedResponse$: Observable<DocsIncludedResponse>;
    entitlementsResponse$: Observable<EntitlementsResult>;
    triggerRowUpdate$: Observable<boolean>;
    private _preferencesResponse: Subject<AccountsResult>;
    private _preferencesSaved: Subject<boolean>;
    private _preferencesConfirmed: Subject<boolean>;
    private _preferencesConfirmedMobile: Subject<boolean>;
    private _error: BehaviorSubject<PershingError>;
    private _loading: BehaviorSubject<boolean>; // Remove this in Sprint 12 when timer is removed
    private _confirmationResponse: Subject<AccountsResult>;
    private _docsIncludedReponse: BehaviorSubject<DocsIncludedResponse>;
    private _entitlementsResponse: Subject<EntitlementsResult>;
    private _triggerRowUpdate: Subject<boolean>;
    private dataStore: {
        preferencesResponse: AccountsResult,
        confirmationResponse: AccountsResult,
        docIncluded: DocsIncludedResponse,
        entitlementsResponse: EntitlementsResult
    };
    defaultPinValue = '0000';
    contextData: Context;


    constructor(private logService: LogService, private http: HttpClient, private authenticationService: IAuthenticationService, private serviceRouter: ServiceRouter) {
        this.initializeObservables();
        this.baseUrl = this.serviceRouter.getServiceRouteURL( EdeliveryServiceDefinition.getInstance());
        // this.baseUrl = 'http://localhost:4200/dev-edc-edelivery-service-service/edc/edelivery';

        // This is the order in which they come from back-end
        this.allDocNamesCurrentlyDisplayed = ['Statements and Reports', 'Trade Confirmations', 'Quarterly Performance Reports', 'Tax Documents', 'Notifications',
                                              'Proxy/Shareholder Communications', 'Prospectus'];
    }

    initializeObservables() {
        this._error = new BehaviorSubject<PershingError>(null);
        this.error$ = this._error.asObservable();
        this.dataStore = { preferencesResponse: null, confirmationResponse: null, docIncluded: null, entitlementsResponse: null };
        this._preferencesResponse = <Subject<AccountsResult>>new Subject();
        this.preferencesResponse$ = this._preferencesResponse.asObservable();
        this._entitlementsResponse = <Subject<EntitlementsResult>>new Subject();
        this.entitlementsResponse$ = this._entitlementsResponse.asObservable();
        this._preferencesSaved = new Subject<boolean>();
        this.preferencesSaved$ = this._preferencesSaved.asObservable();
        this._preferencesConfirmed = new Subject<boolean>();
        this.preferencesConfirmed$ = this._preferencesConfirmed.asObservable();
        this._preferencesConfirmedMobile = new Subject<boolean>();
        this.preferencesConfirmedMobile$ = this._preferencesConfirmedMobile.asObservable();
        this._loading = new BehaviorSubject<boolean>(false);
        this.loading$ = this._loading.asObservable();
        this._showAgreement = new BehaviorSubject<boolean>(false);
        this.showAgreement$ = this._showAgreement.asObservable();
        this._disableEnrollButton = new BehaviorSubject<boolean>(true);
        this.disableEnrollButton$ = this._disableEnrollButton.asObservable();
        this._confirmationResponse = <Subject<AccountsResult>>new Subject();
        this.confirmationResponse$ = this._confirmationResponse.asObservable();
        this._docsIncludedReponse = <BehaviorSubject<DocsIncludedResponse>>new BehaviorSubject(null);
        this.docsIncludedResponse$ = this._docsIncludedReponse.asObservable();
        this._triggerRowUpdate = new Subject<boolean>();
        this.triggerRowUpdate$ = this._triggerRowUpdate.asObservable();
    }

    setContextData(ibd, subscriberId, loginId, businessPartyRole, businessPartyValue, clientId): void {
      this.contextData = new Context();
      this.contextData.ibdId = ibd;
      this.contextData.subscriberId = subscriberId;
      this.contextData.loginId = loginId;
      this.contextData.businessPartyRole = businessPartyRole;
      this.contextData.businessPartyValue = businessPartyValue;
      this.contextData.clientId = clientId;
    }

    isNXIRequest(): boolean {
        if (this.contextData !== undefined) {
            return false;
        }
        return true;
    }

    /**
     * Gets an Observable of type HttpResponse<any> (or) Observable<null> when making the request for preferences.
     * ** catch block is used primarily for observable stream continuation in case of errors during http call
     * @return Observable<HttpResponse<any>> | Observable<{}}>
     */
    retrievePreferences(): void {
        let url: string = this.baseUrl + '/v1/retrievePreferences';
        if (!this.isNXIRequest()) {
            url = url + '/' + this.contextData.ibdId + '/' + this.contextData.subscriberId;
        }
        // let url: string = this.baseUrl + 'https://nonprod14-cluster.bnymellon.net/dev-edc-edelivery-service-service/edc/edelivery/retrievePreferences/6XE/TEST30ACNTS';
        // let url: string = this.baseUrl + 'https://nonprod14-cluster.bnymellon.net/dev-edc-edelivery-service-service/edc/edelivery/retrievePreferences/3CR/EDTEST';
        // let url: string = this.baseUrl + 'https://nonprod14-cluster.bnymellon.net/dev-edc-edelivery-service-service/edc/edelivery/retrievePreferences/3CR/MOHEDEL2';
        // if (this.authenticationService.isLoggedIn()) {
            // this.http.get(`${url}`, { headers: headers })
            if (this.isNXIRequest() && !this.authenticationService.isLoggedIn()) {
                console.log('User not logged in');
                return;
            }
            this.http.post(`${url}`, this.contextData, { headers: headers })
                .subscribe((data: any) => {
                    if (data.errorCode === 0 || data.errorCode === 90) { // success
                        this.logService.info('retrieve preferences success code: ', data.errorCode);
                        this.logService.info('retrieve preferences success data', data);
                        this.clearError();
                        let accountsResult: AccountsResult = defaultAccountsResult();
                        accountsResult.accounts = data.response;
                        this.dataStore.preferencesResponse = accountsResult;
                        if (this.dataStore.preferencesResponse.accounts.length !== 0 && this.dataStore.preferencesResponse.accounts[0].externalEmailAddr) {
                            this.externalEmail = this.dataStore.preferencesResponse.accounts[0].externalEmailAddr.toString();
                        }
                        this.setCorrectNamesDisplayed();
                        this.setAccountsChangedMap(this.dataStore.preferencesResponse.accounts);
                        this._preferencesResponse.next(Object.assign({}, this.dataStore).preferencesResponse);
                        if (!this.backButtonClicked) {
                            this.originalResponse = JSON.parse(JSON.stringify(this.dataStore.preferencesResponse));
                        }
                    } else { // error code from backend
                        this.logService.error('getEdelPreferences() errorCode: ', data.errorCode);
                        this.setError('Failed to retrieve data', data.errorCode);
                    }
                },
                    error => {
                        this.setError(error);
                    });
        // } else {
        //     console.log('User not logged in');
        // }
    }

    updatePreferences(ListOfAccounts): void {
        let url: string = this.baseUrl + '/v1/updatePreferences';
        if (!this.isNXIRequest()) {
            url = url + '/' + this.contextData.ibdId + '/' + this.contextData.subscriberId;
        }
        // let url: string = this.baseUrl + 'https://nonprod14-cluster.bnymellon.net/dev-edc-edelivery-service-service/edc/edelivery/updatePreferences/PPA/JAYANTQA';
        // let url: string = this.baseUrl + 'https://nonprod14-cluster.bnymellon.net/dev-edc-edelivery-service-service/edc/edelivery/updatePreferences/3CR/EDTEST';
        // let url: string = this.baseUrl + 'https://nonprod14-cluster.bnymellon.net/dev-edc-edelivery-service-service/edc/edelivery/updatePreferences/3CR/MOHEDEL2';
        let myList: AccountsResult[] = new Array();
        myList = ListOfAccounts;
        let preferencesRequest: PreferencesRequest = new PreferencesRequest();
        preferencesRequest.accountList = myList;
        preferencesRequest.contextData = this.contextData;
        if (this.isNXIRequest() && !this.authenticationService.isLoggedIn()) {
            console.log('User not logged in');
            return;
        }
        // if (this.authenticationService.isLoggedIn()) {
            // this.http.post(`${url}`, myList, { headers: headers })
            this.http.post(`${url}`, preferencesRequest, { headers: headers })
                .subscribe((data: any) => {
                    if (data.errorCode === 0 || data.errorCode === 90) { // success
                        this.logService.info('update preferences success: ', data);
                        this.logService.log(data);
                        if (data && this.trackingList) {
                            let accountsResult: AccountsResult = defaultAccountsResult();
                            accountsResult.accounts = data.response;
                            this.dataStore.preferencesResponse.accounts = this.trackingList;
                            this.dataStore.confirmationResponse = accountsResult;
                            console.log('this.dataStore.preferencesResponse.accounts: ', this.dataStore.preferencesResponse.accounts);
                            // Confirmation response only contains the documents whose preferences were changed
                            console.log('this.dataStore.confirmationResponse: ', this.dataStore.confirmationResponse);
                            if (this.dataStore.confirmationResponse.accounts[0].externalEmailAddr) {
                                this.externalEmail = this.dataStore.confirmationResponse.accounts[0].externalEmailAddr.toString();
                            }
                            this.isEmailChanged = false;
                            this._preferencesResponse.next(Object.assign({}, this.dataStore).preferencesResponse);
                            this._confirmationResponse.next(Object.assign({}, this.dataStore).confirmationResponse);
                            this.originalResponse = JSON.parse(JSON.stringify(this.dataStore.preferencesResponse));
                            // Second observable for the confirmation
                        } else if (data) {
                            let accountsResult: AccountsResult = defaultAccountsResult();
                            accountsResult.accounts = data.response;
                            this.dataStore.preferencesResponse.accounts = this.trackingList;
                            this.dataStore.confirmationResponse = accountsResult;
                            console.log('this.dataStore.preferencesResponse.accounts: ', this.dataStore.preferencesResponse.accounts);
                            // Confirmation response only contains the documents whose preferences were changed
                            console.log('this.dataStore.confirmationResponse: ', this.dataStore.confirmationResponse);
                            // this.externalEmail = this.dataStore.preferencesResponse.accounts[0].externalEmailAddr.toString();
                            this._preferencesResponse.next(Object.assign({}, this.dataStore).preferencesResponse);
                            this._confirmationResponse.next(Object.assign({}, this.dataStore).confirmationResponse);
                            this.listOfAccountsChanged = [];
                            this.originalResponse = JSON.parse(JSON.stringify(this.dataStore.preferencesResponse));
                        }
                    } else { // error code from backend
                        this.logService.error('getEdelPreferences() errorCode: ', data.errorCode);
                        this.setError('Failed to retrieve data', data.errorCode);
                    }
                    this.updatePreferencesInProgressFlag = false;
                },
                    error => {
                        this.updatePreferencesInProgressFlag = false;
                        this.setError(error);
                    });
        // } else {
        //     console.log('User not logged in');
        // }
        this.retrievePreferences();
    }

    retrieveDocInclusion(docType) {
        let url: string = this.baseUrl + '/retrieveDocinclusion/' + docType;
        if (!this.isNXIRequest()) {
            url = url + '/' + this.contextData.ibdId + '/' + this.contextData.subscriberId;
        }
        if (this.isNXIRequest() && !this.authenticationService.isLoggedIn()) {
            console.log('User not logged in');
            return;
        }
        // let url: string = this.baseUrl + 'https://nonprod14-cluster.bnymellon.net/dev-edc-edelivery-service-service/edc/edelivery/retrieveDocinclusion/PPA/JAYANTQA';
        // let url: string = this.baseUrl + 'https://nonprod14-cluster.bnymellon.net/dev-edc-edelivery-service-service/edc/edelivery/retrieveDocinclusion/3CR/EDTEST';
        // let url: string = this.baseUrl + 'https://nonprod14-cluster.bnymellon.net/dev-edc-edelivery-service-service/edc/edelivery/retrieveDocinclusion/3CR/MOHEDEL2';
        // if (this.authenticationService.isLoggedIn()) {
            this.http.get(`${url}`, { headers: headers })
                .subscribe((data: any) => {
                    if (data.errorCode === 0 || data.errorCode === 90) { // success
                        this.logService.info('retrive doc inclusion success: ', data);
                        this.dataStore.docIncluded = data;
                        this._docsIncludedReponse.next(Object.assign({}, this.dataStore).docIncluded);
                    } else { // error code from backend
                        this.logService.error('getEdelPreferences() errorCode: ', data.errorCode);
                        this.setError('Failed to retrieve data', data.errorCode);
                    }
                },
                    error => {
                        this.setError(error);
                    });
        // } else {
        //     console.log('User not logged in');
        // }
    }

    retrieveEntitlements() {
        let url: string = this.baseUrl + '/retrieveEntitlements';
        if (this.isNXIRequest()) {
            this.logService.info('NXI Request');
            let entitlements: Entitlements = new Entitlements();
            entitlements.fullEnrollEntitlement = true; 
            let entitlementsResult: EntitlementsResult = new EntitlementsResult();
            entitlementsResult.response = entitlements;
            this.dataStore.entitlementsResponse = entitlementsResult;
            this._entitlementsResponse.next(Object.assign({}, this.dataStore).entitlementsResponse);
            return;
        }
        if (this.isNXIRequest() && !this.authenticationService.isLoggedIn()) {
            console.log('User not logged in');
            return;
        }
        // let url: string = this.baseUrl + 'https://nonprod14-cluster.bnymellon.net/dev-edc-edelivery-service-service/edc/edelivery/retrieveDocinclusion/PPA/JAYANTQA';
        // let url: string = this.baseUrl + 'https://nonprod14-cluster.bnymellon.net/dev-edc-edelivery-service-service/edc/edelivery/retrieveDocinclusion/3CR/EDTEST';
        // let url: string = this.baseUrl + 'https://nonprod14-cluster.bnymellon.net/dev-edc-edelivery-service-service/edc/edelivery/retrieveDocinclusion/3CR/MOHEDEL2';
        // if (this.authenticationService.isLoggedIn()) {
            this.http.get(`${url}`, { headers: headers })
                .subscribe((data: any) => {
                    if (data.errorCode === 0 || data.errorCode === 90) { // success
                        this.logService.info('retrieveEntitlements success: ', data);
                        this.dataStore.entitlementsResponse = data;
                        this._entitlementsResponse.next(Object.assign({}, this.dataStore).entitlementsResponse);
                    } else { // error code from backend
                        this.logService.error('retrieveEntitlements() errorCode: ', data.errorCode);
                        this.setError('Failed to retrieve data', data.errorCode);
                    }
                },
                    error => {
                        this.setError(error);
                    });
        // } else {
        //     console.log('User not logged in');
        // }
    }

    setCorrectNamesDisplayed() {
        let accounts = this.dataStore.preferencesResponse.accounts;
        let map = new Map<string, number>();
        for (let j = 0; j < accounts.length; j++) {
            for (let i = 0; i < accounts[j].edeliveryDocuments.length; i++) {
                let document = accounts[j].edeliveryDocuments[i];
                if (document.docEligible === false || accounts[j].edelElgible === false) {
                    let currentValue = map.get(document.edocumentType.docTypeDesc);
                    if (currentValue !== undefined) {
                        map.set(document.edocumentType.docTypeDesc, currentValue + 1);
                    } else {
                        map.set(document.edocumentType.docTypeDesc, 1);
                    }
                    if (map.get(document.edocumentType.docTypeDesc) === accounts.length) {
                        const index = this.allDocNamesCurrentlyDisplayed.indexOf(document.edocumentType.docTypeDesc, 0);
                        if (index > -1) {
                            this.allDocNamesCurrentlyDisplayed.splice(index, 1);
                        }
                    }
                }
                if (document.edocumentType.docTypeCd === 'QPR' && document.docEligible === true) { // Unique case where we need to hide disclaimer if no account is entitled to QPR
                    this.displayQPRDisclaimer = true;
                    this.displayCommunicationTypesDisclaimer = true;
                }

                if ((document.edocumentType.docTypeCd === 'NOTF' && document.docEligible === true) ||
                    (document.edocumentType.docTypeCd === 'TAXD' && document.docEligible === true) ||
                    (document.edocumentType.docTypeCd === 'PROS' && document.docEligible === true)) {
                    this.displayCommunicationTypesDisclaimer = true;
                }
            }
        }
    }

    setAccountsChangedMap(accounts) {
        for (let i = 0; i < accounts.length; i++) {
            this.accountsChangedMap.set(accounts[i].accountNumber, false);
        }
        console.log('accounts changed map: ', this.accountsChangedMap);
    }

    addChangedAccount(account: Account): void {
        // Here we want to add any account that got changed in the preferences so that we know which accounts were changed when clicking save.
        if (this.listOfAccountsChanged.length === 0) {
            this.listOfAccountsChanged.push(account);
        } else {
            for (let i = 0; i < this.listOfAccountsChanged.length; i++) {
                if (this.listOfAccountsChanged[i].accountNumber === account.accountNumber) {
                    this.listOfAccountsChanged[i] = account;
                    break;
                } else if (i === this.listOfAccountsChanged.length - 1) {
                    this.listOfAccountsChanged.push(account);
                }
            }
        }
    }

    // Calculates out of the current preferences which ones are set for every account and marks them as checked in QuickEnroll
    findQuickEnrolled() {
        if (this.previousSettings.length !== 7) { // 7 because that is the exact number of possible document types
            for (let j = 0; j < this.dataStore.preferencesResponse.accounts.length; j++) {
                let account = this.dataStore.preferencesResponse.accounts[j];
                for (let i = 0; i < account.edeliveryDocuments.length; i++) {
                    if (account.edeliveryDocuments[i].docEligible === true) {
                        if (account.edeliveryDocuments[i].preferenceType.deliveryPreference === 2) { // Enrolled
                            this.setQuickEnrollCountValues(account, i);
                        }
                        if (account.edeliveryDocuments[i].edocumentType.docTypeCd === 'PROS') {
                            // Set displayProspectusQuickEnroll to true
                            this.displayProspectusQuickEnroll = true;
                        }
                        this.listOfEligibleDocs.add(account.edeliveryDocuments[i].edocumentType.docTypeDesc);
                    } else { // else only applies when they are not eligible, this is so that the select all check in quick enroll works correctly
                        this.setQuickEnrollCountValues(account, i);
                    }
                }
            }
            for (let i = 0; i < this.dataStore.preferencesResponse.accounts[0].edeliveryDocuments.length; i++) {
                let docName = this.dataStore.preferencesResponse.accounts[0].edeliveryDocuments[i].edocumentType.docTypeDesc;
                if (this.previouslyEnrolled.get(docName) >= this.dataStore.preferencesResponse.accounts.length) {
                    this.previousSettings.push(true);
                    this.currentSettings.push(true);
                } else {
                    this.previousSettings.push(false);
                    this.currentSettings.push(false);
                }
            }
        }
    }

    // Sets count for how many of the documents are previously enrolled so that quick enroll can know whether to auto-check the checkboxes
    setQuickEnrollCountValues(account, docIndex) {
        let value = this.previouslyEnrolled.get(account.edeliveryDocuments[docIndex].edocumentType.docTypeDesc);
        if (value === undefined) {
            this.previouslyEnrolled.set(account.edeliveryDocuments[docIndex].edocumentType.docTypeDesc, 1);
        } else {
            this.previouslyEnrolled.set(account.edeliveryDocuments[docIndex].edocumentType.docTypeDesc, value + 1);
        }
    }

    createTrackingList() {
        if (this.dataStore.preferencesResponse !== null) {
            this.trackingList = JSON.parse(JSON.stringify(this.dataStore.preferencesResponse.accounts));
            console.log('tracking list: ', this.trackingList);
        }
    }

    preferencesSaved(flag) {
        this._preferencesSaved.next(flag);
    }

    preferencesConfirmed(flag) {
        this._preferencesConfirmed.next(flag);
    }

    preferencesConfirmedMobile(flag) {
        this._preferencesConfirmedMobile.next(flag);
    }

    goToAgreement() {
        this.savedSettings = JSON.parse(JSON.stringify(this.currentSettings));
        this._showAgreement.next(true);
        this.backButtonClicked = false;
    }

    backButton() {
        this._showAgreement.next(false);
        this.backButtonClicked = true;
        this.firstCellAccount = true;
    }

    closingDialog() {
        this._showAgreement.next(false);
    }

    /** Check first, to be able to determine whether a user is enrolling or only de-enrolling. */
    checkQuickEnrollPreferences() {
        this.isUserEnrolling = false;
        if (this.accountsMap.size > 0) {
            this.listToBeSubmitted = JSON.parse(JSON.stringify(this.trackingList));
            for (let i = 0; i < this.dataStore.preferencesResponse.accounts.length; i++) {
                this.checkAccountPreferences(this.dataStore.preferencesResponse.accounts[i], this.accountsMap.get(this.dataStore.preferencesResponse.accounts[i].accountNumber), i);
            }
            console.log('final list : ', this.finalListOfAccounts);
        } else {
            console.log('error with quickEnroll');
        }
    }

    submitPreferences() {
        if (!this.updatePreferencesInProgressFlag) {
            this.updatePreferencesInProgressFlag = true;
        } else {
            return;
        }
        this.updatePreferences(this.finalListOfAccounts);
        this.preferencesSubmitted = true;
        this.isUserEnrolling = false;
    }

    checkAccountPreferences(originalAccount, changedAccountDocuments, index) {
        // If account preferences are the same, delete from trackingList
        // Set tempAccount.edeliveryDocuments to none
        this.listToBeSubmitted[index].edeliveryDocuments = [];
        if (changedAccountDocuments !== undefined) {
            for (let j = 0; j < originalAccount.edeliveryDocuments.length; j++) {
                let prefsAreDifferent = originalAccount.edeliveryDocuments[j].preferenceType.deliveryPreference !== changedAccountDocuments[j].preferenceType.deliveryPreference;
                if (prefsAreDifferent) {
                    if (changedAccountDocuments[j].edocumentType.docTypeCd === 'PRXY') {
                    let tempProxy = changedAccountDocuments[j];
                     if (changedAccountDocuments[j].preferenceType.deliveryPreference === 2) {
                        tempProxy.pin = this.defaultPinValue;
                       changedAccountDocuments.push(tempProxy);
                     }
                    }
                    // Only if settings are different, would each account's preferences have changed or if they were pending enrollment
                    if (this.previousSettings[j] !== this.currentSettings[j]) {
                        if (!this.previousSettings[j]) {
                            // Then user is enrolling.
                            this.isUserEnrolling = true;
                        }
                        this.listToBeSubmitted[index].edeliveryDocuments.push(changedAccountDocuments[j]);
                        if (this.finalListOfAccounts.length === 0) {
                            this.finalListOfAccounts.push(this.listToBeSubmitted[index]);
                        } else {
                            this.insertIntoFinalList(this.listToBeSubmitted[index]);
                        }
                    } else if (originalAccount.edeliveryDocuments[j].preferenceType.deliveryPreference === 4) {
                        if (this.currentSettings[j]) {
                            this.isUserEnrolling = true;
                        }
                        this.listToBeSubmitted[index].edeliveryDocuments.push(changedAccountDocuments[j]);
                        if (this.finalListOfAccounts.length === 0) {
                            this.finalListOfAccounts.push(this.listToBeSubmitted[index]);
                        } else {
                            this.insertIntoFinalList(this.listToBeSubmitted[index]);
                        }
                    }
                }
            }
        }
    }

    insertIntoFinalList(accountBeingInserted) {
        for (let x = 0; x < this.finalListOfAccounts.length; x++) {
            if (this.finalListOfAccounts[x].accountNumber === accountBeingInserted.accountNumber) {
                // Only insert the preferences
                this.finalListOfAccounts[x].edeliveryDocuments = accountBeingInserted.edeliveryDocuments;
                return;
            }
        }
        this.finalListOfAccounts.push(accountBeingInserted);
    }

    disableEnrollButtonInEdit(flag) {
        this._disableEnrollButton.next(flag);
    }

    triggerLoadingIcon(flag) {
        this._loading.next(flag);
    }

    triggerRowUpdate(flag) {
        this._triggerRowUpdate.next(flag);
    }

    setError(errorMessage: string, errorCode?: string): void {
        this._error.next(new PershingError(errorMessage, 'AccountsService', errorCode));
    }

    clearError(): void {
        this._error.next(null);
    }

    triggerRefresh(): void {
        // this._preferencesResponse.next(Object.assign({}, this.dataStore).preferencesResponse);
        this._preferencesResponse.next(JSON.parse(JSON.stringify(this.originalResponse)));
    }

    // triggerBackButtonRefresh(originalResponse): void {
    //     // this._preferencesResponse.next(Object.assign({}, this.dataStore).preferencesResponse);
    //     this._preferencesResponse.next(JSON.parse(JSON.stringify(originalResponse)));
    // }

    // resetPreferences() {
    //     this.emitResetPreferences.emit(true);
    //  }
}
